import axios from 'axios'
import store from '@/store'
import axiosRetry from 'axios-retry'
import Cookies from 'js-cookie'
import router from '@/router'
import { isLogin, goToIntro, goToLoginPage, getShopId } from '@/utils'
import { i18n } from '../i18n'

const API = axios.create({
  baseURL: process.env.VUE_APP_BACKEND_APIURL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

API.interceptors.request.use(
  function(config) {
    if (Cookies.get('_ifwt')) {
      if (location.href.indexOf('intro.html') === -1) {
        API.defaults.headers.authorization = `Web ${Cookies.get('_ifwt')}`
      }
    }

    const shopId = getShopId()
    if (shopId) {
      config.headers.shopId = shopId
    }

    return config
  },
  function(error) {
    return Promise.reject(error)
  }
)

API.interceptors.response.use(response => {
  // IE 8-9
  if (
    typeof response.status === 'undefined' &&
    response.data != null &&
    response.request.responseText != null
  ) {
    try {
      response.status = 200
    } catch (e) {
      // ignored
    }
  }
  return response
})
// axios retry를 위한 코드
axiosRetry(API, {
  retires: 3,
  retryCondition: response => {
    // custom
    // timeout도 retry를 해야하나?
    return (
      axiosRetry.isNetworkError(response) || response.code === 'ECONNABORTED'
    )
  },
  shouldResetTimeout: true,
  retryDelay: (error, count) => {
    return 3000
  }
})

/**
 * 연속적인 호출 방지를 위해 해당 함수를 거친다.
 * @param {string} apiName - api 이름
 * @param {object} config - method, url을 담고 있는 객체
 * @param {Function} successCallback - 성공 시 호출할 콜백
 * @param {Function} errorCallback - 실패 시 호출할 콜백
 */
const makeRequestCreator = (
  apiName,
  config,
  successCallback,
  errorCallback
) => {
  // 호출하고 싶은 api의 call 값이 존재 하지 않는다면
  if (!!store.getters['api/getApiCallValue'](apiName) === false) {
    const call = new Date().getTime()
    // alert('apiName', apiName, call)
    return store.dispatch('api/setRequest', { apiName, call }).then(() => {
      return API(config)
        .then(res => handler({ apiName, call }, res, successCallback))
        .catch(err => handler({ apiName, call }, err, errorCallback))
    })
  } else {
    // 이미 호출된 API를 재 호출할 경우 block한다. (infinity scroll 때문에 추가함)
    console.warn('same api call ', apiName, config)
    return false
  }
}

/**
 * 응답을 처리하는 핸들러
 * @param {Object} param0 - api 이름
 * @param {string} param0.apiName - api 이름
 * @param {Number} param0.call - api 호출 시간
 * @param {*} response - 응답 값
 * @param {Function} callback - 실행 할 콜백함수
 */
const handler = ({ apiName, call }, response, callback) => {
  try {
    // 스토어에 저장된 api의 call값과 핸들러가 전달한 call 값이 일치한 경우에만 응답처리를 한다;
    if (store.getters['api/getApiCallValue'](apiName) === call) {
      store.dispatch('api/setRequest', { apiName, call: null })
      // 정상 응답
      if (response.status === 200) {
        // 정상 응답이지만 에러가 있을 경우

        let modalCallback = null
        switch (response.data.code) {
          // switch (201) {
          case 0: // 정상 응답
            callback(response.data)
            break
          case 101: // 비정상 세션
            if (isLogin()) {
              store.dispatch('modal/openModalSessionExpired')
            }
            break
          case 102: // 파라미터 오류
            break
          case 104: // 게스트 계정 구매 제한
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.invalid_account_G04'),
              callback: () => callback(response.data)
            })
            break
          case 201: // 넥슨 쇼핑 전체 점검
            goToIntro()
            break
          case 202: // 넥슨 쇼핑 상점별 점검
          case 203: // 넥슨 쇼핑 상점별 점검
            // callback 없이 점검 페이지로 이동

            if (
              location.href.toLowerCase().indexOf('maintenance') > -1 ||
              apiName === 'getInitData'
            ) {
              callback(response.data)
            } else {
              router.push('/maintenance') // 점검 페이지로 이동
            }

            break
          case 401: // 존재하지 않는 상점
            // 예외처리 jp path에서 존재하지 않는 상점의 경우 hittheworld로 이동 한다.
            if (location.pathname.indexOf('/jp/') > -1) {
              location.href = `${process.env.VUE_APP_HOST}/jp/hittheworld/main`
              return
            }

            alert(i18n.t('alert.invalid_shop_G05'))
            goToIntro()
            break

          case 501: // 존재하지 않는 상품
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.invalid_product_G06'),
              callback: () => {
                history.back()
              }
            })
            break
          case 605: // 결제 실패
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: response.data.message,
              callback: () => callback(response.data)
            })
            break

          case 606: // 만료된 페이지
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: response.data.message,
              callback: () => history.back()
            })
            break

          case 607: // 결제승인되지 않았거나 이미 처리된 토큰입니다 getPaymentResult에서만 발생
            callback(response.data)
            break
          case 712: // 구매내역 조회시 5년 이전을 조회할 경우 발생하는 에러
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.cannot_view_more_than_5_years')
            })
            break
          case 713:
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.end_coupon')
            })
            break
          case 714:
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.expired_coupon')
            })
            break
          case 715:
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.invalid_coupon')
            })
            break
          case 716:
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.end_ten_coupon')
            })
            break
          case 902: // 현금 영수증 발급 에러
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.invalid_receipt')
            })
            break
          case 1000:
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.invalid_coupon_return_to_previous_page'),
              callback: () => {
                history.back()
              }
            })
            break
          case 9102:
            store.dispatch('modal/openModal', {
              type: 'alert',
              message: i18n.t('alert.ims_error')
            })
            break
          default:
            if (apiName === 'getPaymentResult') {
              modalCallback = () => callback(response.data)
            }

            if (apiName === 'getSelectedCharacter') {
              modalCallback = () => callback(response.data)
            }

            store.dispatch('modal/openModal', {
              type: 'alert',
              message: response.data.message,
              callback: modalCallback
            })
            break
        }
      } else if (response.status === 401) {
        goToLoginPage()
      } else {
        // 에러
        callback(response)
      }
    } else {
      console.warn(`${apiName} cancel by user`)
    }
  } catch (e) {
    console.log(e)
  }
}

export { API, makeRequestCreator }
